import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@components/Typography';
import TableContainer from '@mui/material/TableContainer';
import ActionIconButton from '@components/ActionIconButton';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels, ToggleCell, OwnerCell } from '@components/Table';
import { OWNER_TYPE } from '@components/OwnerIcon';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import { RIGHTS } from '@constants/user';
import { CouponType, CouponStatus } from '@constants/coupons';
import { couponsActions } from '@actions';

const getTableHeader = t => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.type')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: 110,
            align: 'center',
            component: (
                <Typography align="center" variant="subtitle2">
                    {t('CouponsPage.issuedCount')}
                </Typography>
            ),
        },
        {
            key: 4,
            width: 110,
            align: 'center',
            component: (
                <Typography align="center" variant="subtitle2">
                    {t('CouponsPage.redeemedCount')}
                </Typography>
            ),
        },
        {
            key: 5,
            width: 90,
            align: 'center',
            component: (
                <Typography align="center" variant="subtitle2">
                    {t('common.enabled')}
                </Typography>
            ),
        },
        {
            key: 6,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const getTableBody = (data, companies, brands, onClickAction, t, hasAccessToAssetById, statusPendingIds) => {
    return data.map(coupon => {
        const brand = coupon.locationId ? null : brands?.find(b => b.id === coupon.brandId);
        const ownerName = brand
            ? brand.title
            : companies?.find(company => company.companyId === coupon.locationId)?.internalName;
        const rights = hasAccessToAssetById(coupon, 'coupons');
        const CouponTypeMap = {
            [CouponType.PercentageOff]: t('CouponsPage.percentageOff'),
            [CouponType.AmountOff]: t('CouponsPage.amountOff'),
            [CouponType.FreeItem]: t('CouponsPage.freeItem'),
        };

        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {coupon.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <OwnerCell
                        type={brand ? OWNER_TYPE.BRAND : OWNER_TYPE.LOCATION}
                        name={ownerName || '--'}
                        sx={{ maxWidth: 400, '@media (max-width: 1024px)': { maxWidth: 200 } }}
                    />
                ),
            },
            {
                component: (
                    <Typography style={{ width: 220 }} variant="body2" align="left">
                        {CouponTypeMap[coupon.type]}
                    </Typography>
                ),
            },
            {
                align: 'center',
                component: (
                    <Typography variant="body2" align="center">
                        {coupon.issuedCouponsCount}
                    </Typography>
                ),
            },
            {
                align: 'center',
                component: (
                    <Typography variant="body2" align="center">
                        {coupon.redeemedCouponsCount}
                    </Typography>
                ),
            },
            {
                align: 'center',
                component: (
                    <ToggleCell
                        checked={coupon.status === CouponStatus.Active}
                        disabled={!rights[RIGHTS.WRITE] || statusPendingIds.includes(coupon.id)}
                        onChange={event => onClickAction(coupon, event.target.checked ? 'resume' : 'pause')}
                    />
                ),
            },
            {
                component: (
                    <Typography align="right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <ActionIconButton
                            title={t('buttons.download')}
                            onClick={() => onClickAction(coupon, 'download')}
                        >
                            <DownloadIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                        <ActionIconButton title={t('common.view')} onClick={() => onClickAction(coupon, 'view')}>
                            <VisibilityIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                        {rights[RIGHTS.WRITE] && (
                            <ActionIconButton title={t('common.edit')} onClick={() => onClickAction(coupon, 'edit')}>
                                <EditIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        )}
                        {rights[RIGHTS.WRITE] && (
                            <ActionIconButton
                                title={t('common.delete')}
                                onClick={() => onClickAction(coupon, 'archive')}
                            >
                                <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                            </ActionIconButton>
                        )}
                    </Typography>
                ),
            },
        ];
    });
};

const CouponsTable = ({
    setPage,
    page,
    setRowsPerPage,
    rowsPerPage,
    onClickCoupon,
    companies,
    brands,
    statusPendingIds,
}) => {
    const { t } = useTranslation();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const { data, loading, totalCount, pages } = useSelector(state => state.coupons);

    const dispatch = useDispatch();

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(couponsActions.get(newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(couponsActions.get(0, parseInt(event.target.value, 10)));
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={getTableHeader(t)}
                data={data}
                rows={getTableBody(data, companies, brands, onClickCoupon, t, hasAccessToAssetById, statusPendingIds)}
                onRowClick={coupon => onClickCoupon(coupon, 'view')}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 7,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default CouponsTable;
